import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Build as BuildIcon,
  Pending as PendingIcon,
  CheckCircle as CompletedIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import api from '../../services/api';

const MAINTENANCE_CATEGORIES = [
  'Plumbing - Leaking tap/pipe',
  'Plumbing - Blocked drain/toilet',
  'Plumbing - No water supply',
  'Electrical - Power outage',
  'Electrical - Faulty socket/switch',
  'Electrical - Light not working',
  'Door - Broken lock',
  'Door - Door not closing properly',
  'Window - Broken glass',
  'Window - Lock not working',
  'Appliances - Fridge/Stove not working',
  'Walls - Cracks/Damage',
  'Walls - Paint peeling',
  'Ceiling - Water damage/Leaks',
  'Floor - Broken tiles',
  'Pest Control - Cockroaches/Rats',
  'Security - Gate/Fence repair',
  'Other',
];

const PRIORITY_LEVELS = [
  { value: 'low', label: 'Low - Can wait a few days', color: 'info' },
  { value: 'medium', label: 'Medium - Within this week', color: 'warning' },
  { value: 'high', label: 'High - Urgent (24-48 hours)', color: 'error' },
  { value: 'emergency', label: 'Emergency - Immediate attention', color: 'error' },
];

const TenantMaintenance = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    priority: 'medium',
    description: '',
    location: '',
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      // Use tenant-specific endpoint that only returns this tenant's requests without cost info
      const response = await api.get('/api/maintenance/me');
      setRequests(response.data || []);
    } catch (error) {
      console.log('No maintenance requests found:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      category: '',
      priority: 'medium',
      description: '',
      location: '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Use tenant-specific POST endpoint
      await api.post('/api/maintenance/me', formData);
      toast.success('Maintenance request submitted successfully!');
      handleCloseDialog();
      fetchRequests();
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    const level = PRIORITY_LEVELS.find(p => p.value === priority);
    return level?.color || 'default';
  };

  const pendingRequests = requests.filter(r => r.status === 'pending').length;
  const completedRequests = requests.filter(r => r.status === 'completed').length;

  if (loading) {
    return <Loading message="Loading maintenance requests..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Maintenance Requests"
        subtitle="Submit and track maintenance requests"
        breadcrumbs={[{ label: 'Maintenance' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            New Request
          </Button>
        }
      />

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total Requests
                  </Typography>
                  <Typography variant="h4">{requests.length}</Typography>
                </Box>
                <BuildIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Pending
                  </Typography>
                  <Typography variant="h4">{pendingRequests}</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 48, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Completed
                  </Typography>
                  <Typography variant="h4">{completedRequests}</Typography>
                </Box>
                <CompletedIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Requests Table */}
      {requests.length === 0 ? (
        <EmptyState
          title="No Maintenance Requests"
          description="You haven't submitted any maintenance requests yet. Click 'New Request' to report an issue."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Submit First Request
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Request ID</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>REQ-{request.id}</TableCell>
                  <TableCell>{request.category}</TableCell>
                  <TableCell>
                    <Chip
                      label={request.priority?.toUpperCase()}
                      color={getPriorityColor(request.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      {request.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status?.toUpperCase().replace('_', ' ')}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(request.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Request Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Maintenance Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Category *"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            margin="normal"
            required
          >
            {MAINTENANCE_CATEGORIES.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Priority *"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            margin="normal"
            required
            helperText="Select urgency level for this request"
          >
            {PRIORITY_LEVELS.map((level) => (
              <MenuItem key={level.value} value={level.value}>
                {level.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Location/Room"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            margin="normal"
            placeholder="e.g., Room 101, Kitchen, Bathroom"
          />

          <TextField
            fullWidth
            label="Description *"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            required
            multiline
            rows={4}
            placeholder="Please describe the issue in detail..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.category || !formData.description}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantMaintenance;
